import type { Product, SubProduct, ProductConditionParams } from '@nf/types/product';
import { thirdPartyProducts as thirdPartyProductsSetting } from '@/settings/products/third-party-products';
import { productStyle as style } from './product.style';
import { SwiperContainer } from '@/components/containers/swiper-container';
import Image from 'next/image';
import { Icon, LiveIcon } from '@/components/icons/svg-icons';
import { cx } from 'styled-system/css';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { Tabs, Tab } from '@/jupiter/components/tabs';
import { useLocale, useTranslations } from 'next-intl';
import { OpenTypeEnum } from '@/types/template-resource';
import { useTemplateResourceContext } from '../context/template-resource-context';
import { siteService } from '@/services/site.service';
import { sportService } from '@/services/sport.service';
import { AthenaSiteNameEnums } from '@nf/types/site';
import { useRouter } from '@/navigation';
import { useEffect, useState, useMemo, Fragment } from 'react';
import { useAccountInfo } from '@/hooks/query/account';
import { useCasinoRoyale } from '@/hooks/query/use-casino-royale';
import { useSiteStore } from '@/store/site';
import { useAuthToken } from '@/store/auth';
import { MarketData, useOriginMenuData } from '@/hooks/query';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote } from '@nf/utils-common/constants';
import { ViewEnum } from '@/types';
import { ProvidedMarket } from '@/settings/menu-type-settings';
import { CdnImg } from '@/features/cdn-img';
import { EventMode, RawMenuData } from '@/types/menu-setting';
import { sportsbookService } from '@/services/sportsbook.service';
import { getForbiddenSport, isNumberGames } from '@/services/settings.service';
import { useAppearanceStore } from '@/store/appearance';
import { modalService } from '@/services/modal.service';
import { useFilterSabaString } from '@/hooks/query/use-saba-filter';
import { HomePageProduct } from '../types/products';
import { defaultImageService } from '@/services/jupiter/default-image-service';
import { useMemoizeArgs } from '@/jupiter/hooks/use-memo-args';
import clsx from 'clsx';
import { BetBuilderPlusIcon } from '@/components/icons/bet-builder-plus-icon';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { NewFeatureBadge } from '@/components/new-tag/new-feature-badge';
import { usePredictionMarket } from '@/hooks/use-prediction-market';
import { openAdvantPlay } from '@/services/third-party-entrance.service';
import { featureNewTagMap } from '@/settings/sports/sport-new-tag-map';
import { ExclusiveActivityBanner } from '@/features/home-page/exclusive-activity-banner';
import { WcProductTag } from '@/features/wc-promotion-2026/components/wc-tag/wc-tag';
import { useWcPromotionVisible } from '@/domains/wc-promotion/hooks/use-wc-promotion-visible';
import { wcPromotionMissionService } from '@/domains/wc-promotion/services/wc-promotion-mission.service';
import { TaskTypeEnum } from '@/domains/wc-promotion/types';
import { SINGLE_SPORT_MENU_MODE } from '@/constants/sport-menu-mode';
import { useMenuTypeList } from '@/hooks/query';

const productTypeList = [
	{ Label: 'Sports', IconName: 'sports' },
	{ Label: 'Games', IconName: 'casino' }
]

const marketDefinition: { [key: string]: { label: string, index: number, isBetBuilder?: boolean } } = {
	L: {
		label: 'lbl_live',
		index: 0
	},
	T: {
		label: 'lbl_today',
		index: 1
	},
	E: {
		label: 'lbl_early',
		index: 2
	},
	P: {
		label: 'lbl_mixparlay',
		index: 3
	},
	BB: {
		label: 'lbl_betbuilderplus',
		index: 4,
		isBetBuilder: true
	}
};

const sportEnNameResourceKeys: { [key: string]: string } = {
	sport1: 'Soccer',
	sport997: 'Soccer',
	sport2: 'Basketball',
	sport993: 'Basketball',
	sport3: 'Football',
	sport4: 'Ice Hockey',
	sport5: 'Tennis',
	sport6: 'Volleyball',
	sport7: 'Snooker',
	sport8: 'Baseball',
	sport9: 'Badminton',
	sport10: 'Golf',
	sport11: 'Motorsports',
	sport12: 'Swimming',
	sport13: 'Politics',
	sport14: 'WaterPolo',
	sport15: 'Diving',
	sport16: 'Boxing',
	sport17: 'Archery',
	sport18: 'Table Tennis',
	sport19: 'Weightlifting',
	sport20: 'Canoeing',
	sport21: 'Gymnastics',
	sport22: 'Athletics',
	sport23: 'Equestrian',
	sport24: 'Handball',
	sport25: 'Darts',
	sport26: 'Rugby',
	sport27: 'Cricket',
	sport28: 'Field Hockey',
	sport29: 'Winter Sports',
	sport30: 'Squash',
	sport31: 'Entertainment',
	sport32: 'Netball',
	sport33: 'Cycling',
	sport34: 'Fencing',
	sport35: 'Judo',
	sport36: 'M. Pentathlon',
	sport37: 'Rowing',
	sport38: 'Sailing',
	sport39: 'Shooting',
	sport40: 'Taekwondo',
	sport41: 'Triathlon',
	sport42: 'Wrestling',
	sport43: 'E-Sports',
	sport44: 'Muay Thai',
	sport45: 'Beach Volleyball',
	sport47: 'Kabaddi',
	sport48: 'SepakTakraw',
	sport49: 'Futsal',
	sport50: 'Cricket',
	sport51: 'Beach Soccer',
	sport52: 'Poker',
	sport53: 'Chess',
	sport54: 'Olympics',
	sport55: 'Finance',
	sport99: 'Other Sports',
	sport161: 'Number Game',
	sport164: 'Happy 5',
	sport175: 'BitCoin',
};

const getProductGameImageId = (gameId: number, enableFilterSaba?: boolean): string => {
	if (gameId === 997 && enableFilterSaba) {
		return 'sport997-special';
	}
	return `sport${gameId}`;
};

const getAvailableMarketsInPriority = (matchCountOfMarkets: Record<string, number>): string[] => {
	return Object.keys(matchCountOfMarkets)
		.filter(market => Object.prototype.hasOwnProperty.call(marketDefinition, market) && (matchCountOfMarkets[market] ?? 0 > 0))
		.sort((marketA, marketB) => marketDefinition[marketA].index - marketDefinition[marketB].index);
};

export type ThirdPartyProductProps = {
	eventMode?: string,
	GameId: number | string,
	GlobalResourceLabel: string,
	BackgroundName?: string,
	Count?: number,
	RouteTarget?: string,
	IsLive?: boolean,
	forAnalyse?: boolean
}

export type NonSportProductProps = {
	eventMode?: string,
	GameId: number | string,
	GlobalResourceLabel: string,
	BackgroundName?: string,
	Count?: number,
	RouteTarget?: string,
	IsLive?: boolean,
	forAnalyse?: boolean,
	Url?: string,
	Name?: string,
	Sort?: number,
	isNew?: boolean,
}

type ProductProps = {
	currentEventMode: string | undefined,
	currentMarket: string | undefined,
	currentRoutePrefix: string | undefined,
	setCurrentMarket: (market: string) => void,
	matchCountOfMarkets: Record<string, number>,
	availableProducts: MarketData[],
	setThirdPartyProduct: (products: ThirdPartyProductProps[] | undefined) => void,
	availableEventModes: EventMode[],
	thirdPartyProducts: ThirdPartyProductProps[],
	setCurrentEventMode: (eventMode: EventMode) => void,
	setIsContributor: (contributor: boolean) => void,
	isContributor: boolean | undefined,
	nonSportProducts: NonSportProductProps[],
	setNonSportProducts: (nonSportProducts: NonSportProductProps[]) => void,
	commingSoccerName?: string
}

export const ProductsWide = ({ matchCountOfMarkets, availableProducts, currentEventMode, setCurrentMarket, availableEventModes, currentMarket, currentRoutePrefix, setThirdPartyProduct, thirdPartyProducts, setCurrentEventMode, setIsContributor, isContributor, nonSportProducts, setNonSportProducts, commingSoccerName }: ProductProps) => {
	const router = useRouter();
	const locale = useLocale();
	const t = useTranslations('General');
	const { SiteConfigsData } = useGetSiteConfigs();
	const activeFilterBetBuilderPlus = useSiteStore(state => state.activeFilterBetBuilderPlus);
	const activeMatches = useSiteStore(state => state.activeMatches);
	const { filterSabaString } = useFilterSabaString();
	const templateResourceContext = useTemplateResourceContext();
	const { productType, setProductType, productId, setProductId, templateResource, isSuccess } = templateResourceContext;
	const [selectedProduct, setSelectedProduct] = useState(productId);
	const { user } = useAccountInfo();
	const [currentProduct, setCurrentProduct] = useState('');
	const [products, setProducts] = useState<Product[]>([]);
	const view = useAppearanceStore(state => state.view);
	const oddsPageLayout = useAppearanceStore(state => state.oddsPageLayout);
	const isNewbie = oddsPageLayout === 'list';
	const forbiddenSport = getForbiddenSport(view);
	const [available3rdPartyGames, setAvailable3rdPartyGames] = useState<Array<number>>([]);
	const [homePageProducts, setHomePageProducts] = useState<Array<HomePageProduct>>([]);
	const { menuResponse } = useOriginMenuData({ isNeedParlayData: false });
	const isBefore = !useAuthToken();
	const { enabledBetBuilderPlusSportIDs, enableESoccerBattle, nonSport } = useLicSettings();
	const [
		deepLinkSiteInfo
	] = useSiteStore(state => [
		state.deepLinkSiteInfo
	]);
	const { canSeePredictionMarket } = usePredictionMarket();
	const { openCasinoRoyale } = useCasinoRoyale();
	const isWcPromotionVisible = useWcPromotionVisible();
	const { menuTypeList } = useMenuTypeList();

	const productConditionParams = useMemo((): ProductConditionParams => ({
		available3rdPartyGames,
		virtualSoccerList: nonSport?.virtualSoccerList,
		canSeeCasinoRoyale: SiteConfigsData?.CanSeeCasinoRoyale === true,
		canSeePredictionMarket: canSeePredictionMarket === true,
		enableSabaScore: SiteConfigsData?.EnableSabaScore === true,
		forbiddenSport,
		isEuroCupEnabled: availableEventModes.indexOf('M3') !== -1,
		isNewbie,
		enableESoccerBattle: enableESoccerBattle ?? false,
		siteId: SiteConfigsData?.GetMessageID ?? '0',
		user,
		canSeeAdvantPlayVendorLobby: Boolean(user?.CanSeeAdvantPlayVendorLobby || SiteConfigsData?.CanSeeAdvantPlayVendorLobby),
	}), [
		available3rdPartyGames,
		nonSport?.virtualSoccerList,
		SiteConfigsData?.CanSeeCasinoRoyale,
		canSeePredictionMarket,
		SiteConfigsData?.EnableSabaScore,
		forbiddenSport,
		availableEventModes,
		isNewbie,
		enableESoccerBattle,
		SiteConfigsData?.GetMessageID,
		user,
		SiteConfigsData?.CanSeeAdvantPlayVendorLobby,
	]);

	const checkProductEnabled = (product: Product | SubProduct): boolean => {
		if (product.enabled !== undefined) {
			return product.enabled;
		}

		if (product.subProducts) {
			return product.subProducts.some(
				subProduct => subProduct.enableCondition?.(productConditionParams)
			);
		}

		return product.enableCondition?.(productConditionParams) ?? false;
	};

	useEffect(() => {
		if (isSuccess && selectedProduct !== undefined) {
			const resource = templateResource.ProductResources.find(productResource => productResource.Name === homePageProducts[selectedProduct]?.Name);
			const selectedProductItem = homePageProducts[selectedProduct];
			if (selectedProductItem?.Id !== undefined && /^M\d+$/.test(selectedProductItem.Id)) {
				const eventMode = selectedProductItem.Id as EventMode;
				setCurrentEventMode(eventMode);
				setProductId(undefined);
			} else if (resource && resource.OpenType == OpenTypeEnum.Inner) {
				setProductId(resource.Id);
			} else {
				setCurrentEventMode('M0' as EventMode);
			}
		}
	}, [selectedProduct, isSuccess]);

	useEffect(() => {
		if (isSuccess && products.length > 0) {
			getHomePageProducts();
			const Id = templateResource.ProductResources.find(productResource => productResource.OpenType == OpenTypeEnum.Inner)?.Sort;
			if (Id) {
				const index = templateResource.ProductResources.map(productResource => productResource.Sort).indexOf(Id);
				setSelectedProduct(index);
				onOptionClick(index);
			}
		}
	}, [isSuccess, products, useMemoizeArgs(templateResource?.ProductResources)]);

	useEffect(() => {
		const availableMarketsInPriority = getAvailableMarketsInPriority(matchCountOfMarkets);
		if (availableMarketsInPriority.length > 0) {
			if (!currentMarket || availableMarketsInPriority.every(availableMarket => availableMarket !== currentMarket)) {
				setCurrentMarket(availableMarketsInPriority[0]);
			}
		}
	}, [matchCountOfMarkets]);

	useEffect(() => {
		setProducts(
			availableEventModes.length
				? [
					...(['M0', ...availableEventModes.filter(mode => !['M0', 'M27', 'M28'].includes(mode as string))]
						.filter((mode, i, arr) => availableEventModes.includes(mode as any) && arr.indexOf(mode) === i)
						.map((menuType) => {
							const modeId = menuType.replace(/^M(\d+)$/, '$1');
							const isAllSports = modeId === '0';
							return {
								productName: `M${modeId}`,
								image: {
									href: sportService.getHomeEventModeIcon(modeId),
								},
								resourceKey: isAllSports
									? 'lbl_Sports'
									: sportsbookService.makeMenuNodeResourceKey(SiteConfigsData?.EnableFilterSaba, modeId),
							};
						})
					),
					...thirdPartyProductsSetting.filter(checkProductEnabled),
				]
				: []
		);
	}, [availableEventModes, productConditionParams]);

	useEffect(() => {
		const currentAvailable3rdPartyGames: Array<number> = menuResponse.reduce((available3rdPartyGames: Array<number>, rawMenuData: RawMenuData): Array<number> => {
			const { GameId } = rawMenuData;
			if ((isNumberGames(GameId) || sportService.isVirtualSports(GameId)) && !available3rdPartyGames.includes(GameId)) {
				available3rdPartyGames.push(GameId);
			}
			return available3rdPartyGames;
		}, []);
		if (available3rdPartyGames.length !== currentAvailable3rdPartyGames.length || currentAvailable3rdPartyGames.some(currentAvailable3rdPartyGame => !available3rdPartyGames.includes(currentAvailable3rdPartyGame))) {
			setAvailable3rdPartyGames(currentAvailable3rdPartyGames);
		}

	}, [menuResponse]);

	useEffect(() => {
		if (products.length > 0) {
			if (!currentProduct || products.every(existingProduct => existingProduct.productName !== currentProduct)) {
				const sort = templateResource.ProductResources.find(productResource => productResource.OpenType == OpenTypeEnum.Inner)?.Sort;
				if (sort) {
					const index = templateResource.ProductResources.map(productResource => productResource.Sort).indexOf(sort);
					onOptionClick(index);
				}
			}
		}
	}, [currentProduct, products]);

	const getHomePageProducts = () => {
		if (templateResource.ProductResources) {
			setHomePageProducts([]);
			const productElements: Array<HomePageProduct> = [];
			templateResource.ProductResources.map(item => {
				if (item.TheURL?.toLowerCase() == 'sports') {
					products
						.filter(product => /^M\d+$/.test(product.productName))
						.map(product => {
							productElements.push({
								Id: product.productName,
								Name: filterSabaString(t(product.resourceKey)),
								ClassName: product.image.href,
								OpenType: 0,
								TheURL: '',
								SiteConfigKey: item.SiteConfigKey
							})
						});
				} else {
					productElements.push({
						Name: item.Name,
						ClassName: `${SiteConfigsData?.ImageServerUrl}${item.ClassName}`,
						OpenType: item.OpenType,
						TheURL: item.TheURL,
						SiteConfigKey: item.SiteConfigKey
					});
				}
			});
			setHomePageProducts(productElements);
		}
	}

	const onOptionClick = (index: number) => {
		if (!isSuccess || homePageProducts.length === 0 || (homePageProducts[index] && homePageProducts[index].OpenType === OpenTypeEnum.Popup)) {
			return;
		} 
		const selectedOption = products.find(product => 
			product.productName === homePageProducts[index]?.Id ||
			product.productName.toLowerCase() === homePageProducts[index]?.Name?.replace(' ', '').toLowerCase()
		);
		if (selectedOption) {
			const optionName = selectedOption.productName;
			setKafka(optionName);
			setCurrentProduct(optionName);
			if (selectedOption.subProducts) {
				const visibleProductProps = selectedOption.subProducts.filter(checkProductEnabled);

				if (visibleProductProps.length > 0) {
					// virtual sports 有項目才顯示 VSTournament，並顯示在第一順位
					if (selectedOption.productName == 'virtualSports' && user?.VSTournament && user?.ActStatus == 2) {
						visibleProductProps.unshift({
							productName: 'vsTournament',
							GameId: 'virtual-tournament',
							GlobalResourceLabel: 'lbl_VSTournament',
							RouteTarget: '/third-party/vs-tournament'
						});
					}
					
					setThirdPartyProduct(
						visibleProductProps.map(productProps => ({
							...productProps,
							GlobalResourceLabel: t(productProps.GlobalResourceLabel)
						}))
					);
				}
			}
		} else {
			setThirdPartyProduct([]);
			setNonSportProducts([]);
		}
	}

	const clickMarket = (market: string) => {
		setKafka(market);
		setCurrentMarket(market);
		if (market === 'BB') {
			wcPromotionMissionService.notifyMission({
				taskType: TaskTypeEnum.ClickSabaFeatures,
				productCode: 'bet-builder'
			});
		}
		if (market === 'P') {
			wcPromotionMissionService.notifyMission({
				taskType: TaskTypeEnum.ClickSabaFeatures,
				productCode: 'parlay'
			});
		}
	}

	const setKafka = (market: string) => {
		let action;
		switch (market) {
			case 'L': // live
				action = 7;
				break;
			case 'T': // today
				action = 8;
				break;
			case 'E': // early
				action = 9;
				break;
			case 'P': // parlay
				action = 10;
				break;
		}
		if (action) {
			const kafkaData = {
				note: KafkaNote.MarsV1,
				type: 1,
				action
			}
			setKafkaNormal(kafkaData);
		}
	}

	const handleSubProductClick = (route: string, hasIsBefore: boolean, gameId?: number) => {
		const isBetBuilderPlusTab = currentMarket === 'BB'; // 待確認是否要改名 	

		if (isBefore && hasIsBefore) {
			modalService.openLoginModal();
			return;	
		}

		if (gameId === 1) {
			setKafkaNormal(
				view === ViewEnum.galaxy
					? { note: KafkaNote.GalaxyV2, type: 16, action: 2 }
					: { note: KafkaNote.MarsV1, type: 1, action: 21 }
			);
		}

		if (route) {

			if (isBetBuilderPlusTab) {
				activeFilterBetBuilderPlus();
			} else {
				activeMatches();
			}
			
			router.push(route, { locale })
		}
	}

	useEffect(() => {
		const isContributorTemp = (selectedProduct !== undefined && Boolean(products.find(product => product.productName === homePageProducts[selectedProduct]?.Id ? homePageProducts[selectedProduct].Id : '')));
		setIsContributor(
			isContributorTemp
		);
	}, [selectedProduct, products, homePageProducts]);

	let sortedAvailableProducts = [...availableProducts];
	const sportOrderList = [1, 2, 6, 9, 12, 22, 16, 43, 21, 19, 17];
	if (currentEventMode == 'M14') {
		sortedAvailableProducts = sortedAvailableProducts.sort((a, b) => {
			const aIndex = sportOrderList.indexOf(a.GameId ?? 0);
			const bIndex = sportOrderList.indexOf(b.GameId ?? 0);
			if (aIndex === -1 && bIndex === -1) return 0;
			if (aIndex === -1) return 1;
			if (bIndex === -1) return -1;
			return aIndex - bIndex;
		});
	}

	return (
		<section data-name="product" className={style.product}>
			{siteService.isMatchingSite(SiteConfigsData?.GetMessageID, AthenaSiteNameEnums.Macaubet) ? (
				<Tabs>
					{ productTypeList.map((type, index) => (
						<Tab key={`product-type-${index}`} label={type.Label} iconName={type.IconName} isSelected={productType == type.Label} onClick={() => {
							setProductType(type.Label);
						}}
						/>
					)) }
				</Tabs>
			) : null}
			{
				isSuccess &&
					<SwiperContainer
						currentIndex={selectedProduct}
						slideElements={homePageProducts.map((product) => (<Option key={product.Name} item={product}></Option>))}
						className={cx('home-nav', style.optionsSwiper)}
						elementClassName={style.optionItem}
						customProps={{
							navigation: { enabled: true },
							allowTouchMove: true,
						}}
						disableOnClickIdx={homePageProducts.map((product, index) => { 
							if (product.OpenType === OpenTypeEnum.Popup) {
								return index;
							} return undefined;
						}).filter((index): index is number => index !== undefined)}
						onSlideClick={(index: number) => {
							const prevIndex = selectedProduct;
							// 單一運動選單，直接跳轉到第一個子選單
							if (SINGLE_SPORT_MENU_MODE.includes(homePageProducts[index].Id as EventMode)) {
								const modeId = Number(homePageProducts[index].Id?.replace(/^M(\d+)$/, '$1'));
								const path = menuTypeList.find(item => item.modeId === modeId)?.path;
								if (path) {
									if (modeId === 50 && view === ViewEnum.galaxy) {
										setKafkaNormal({
											note: KafkaNote.GalaxyV2,
											type: 16,
											action: 3
										});
									} else if (modeId === 50 && view === ViewEnum.mars) {
										setKafkaNormal({
											note: KafkaNote.MarsV1,
											type: 1,
											action: 26
										});
									}
									router.push(path, { locale });
								}
								return;
							}
							setSelectedProduct(index);
							const currentProudct = homePageProducts[index];
							if (currentProudct.SiteConfigKey === 'CanSeeVirtualSports') {
								wcPromotionMissionService.notifyMission({
									taskType: TaskTypeEnum.ClickVirtualSports,
									productCode: 'virtualSports'
								});
							} 
							switch (currentProudct.OpenType) {
								case OpenTypeEnum.Inner:
									break;
								case OpenTypeEnum.Popup:
									if (prevIndex !== undefined) {
										setSelectedProduct(prevIndex);
									} else {
										setSelectedProduct(0);
									}
									break;
								default:
									break;
							}
							onOptionClick(index);
						}}
					/>
			}
			<div data-name="product_market" className={style.market}>
				{
					isContributor &&
					<div className={style.marketGroup}>
						{
							Object.keys(matchCountOfMarkets)
								.filter(market => {
									return Object.prototype.hasOwnProperty.call(marketDefinition, market) && (matchCountOfMarkets[market] ?? 0 > 0);
								})
								.sort((marketA, marketB) => marketDefinition[marketA].index - marketDefinition[marketB].index)
								.map(market => {
									const isLiveMarket = market === 'L';
									const isBetBuilder = marketDefinition[market].isBetBuilder ?? false;
									const isSelected = market === currentMarket;
									return (
										<div 
											key={market}
											className={cx('group', style.marketItem)} 
											data-active={isSelected}
											data-live={isLiveMarket}
											onClick={() => clickMarket(market)}
										>
											{
												!isLiveMarket ? 
													null :
													<div className="live-icon-animation">
														<Icon name="filter-live" />
													</div>
											}
											{isBetBuilder ? (
												<>
													<BetBuilderPlusIcon />
													<NewFeatureBadge timeRange={featureNewTagMap.betBuilderPlusBasketball} />
												</>
											): null}
											<div className={style.marketLabel}>{t(marketDefinition[market].label)}</div>
											<div className={style.marketCount}>{matchCountOfMarkets[market]}</div>
										</div>
									);
								})
						}
					</div>
				}
			</div>

			{homePageProducts[selectedProduct ?? 0]?.Id === 'M0' || homePageProducts[selectedProduct ?? 0]?.Id === 'M2' ? (
				<div data-name="product_activity-banner" className={style.activityBanner}>
					<ExclusiveActivityBanner />
				</div>
			) : null}

			<div data-name="product_productList" className={style.productList}>
				{
					isContributor ?
						sortedAvailableProducts
							.filter(marketData => sportEnNameResourceKeys[`sport${marketData.GameId}`] !== undefined)
							.filter((marketData, productIndex) => productIndex < 21)
							.filter(marketData => (currentMarket === 'BB' && marketData.GameId === 1 && !enabledBetBuilderPlusSportIDs?.includes(1)) ? false : true)
							.map((marketData) => {
								const { GameId, Name } = marketData;
								const isCurrentlyShowingLive = currentMarket === 'L';
								const preferredMarket = (currentMarket !== 'P' && currentMarket !== 'BB') ? currentMarket : ['L', 'T', 'E'].find((filterableMarket: string): boolean => (marketData[filterableMarket as ProvidedMarket] ?? 0) > 0);
								const count = (currentMarket === 'P' || currentMarket === 'BB') ? ['E', 'L', 'T'].reduce((sum, market) => sum += marketData[market as ProvidedMarket], 0) : marketData[currentMarket as ProvidedMarket];
								const url = `/images/home/product-game/${getProductGameImageId(GameId, SiteConfigsData?.EnableFilterSaba)}.webp`;
								const route = (!preferredMarket || !currentRoutePrefix) ? '' : (isCurrentlyShowingLive && currentRoutePrefix === 'sports') ? `/live/${GameId}` : `/${currentRoutePrefix}/${GameId}/${preferredMarket.toLowerCase()}`
								return (
									<Fragment key={GameId}>
										<div
											data-name="product_productItem"
											key={GameId}
											className={cx('group', style.productItem)}
											onClick={() => handleSubProductClick(route, false, GameId)}
										>
											<div className={style.productBg}>
												<div className="home-product__game-img" />
												<CdnImg
													width={172}
													height={88}
													src={url}
													alt=""
													onError={(e) => defaultImageService.setDefaultImageUrl(e, SiteConfigsData?.ImageServerUrl)}
												/>
												<div className={style.productCount}>{count}</div>
											</div>
											<div className={style.productName}>{Name}</div>
											{GameId === 1 && isWcPromotionVisible ? <WcProductTag /> : null}
										</div>
										{GameId === 2 && currentMarket === 'BB' && !enabledBetBuilderPlusSportIDs?.includes(1) ? 
											(
												<div
													data-name="product_productItem"
													data-disabled={true}
													key={'coming-soon-soccer'}
													className={cx('group', style.productItem)}
													onClick={() => handleSubProductClick('', false)}
												>
													<div className={style.productBg}>
														<div className="home-product__game-img" />
														<CdnImg
															width={172}
															height={88}
															src="/images/home/product-game/sport1.webp"
															alt=""
															onError={(e) => defaultImageService.setDefaultImageUrl(e, SiteConfigsData?.ImageServerUrl)}
														/>
														<div className={clsx(style.productItemTag)}>{t('lbl_ComingSoon')}</div>
													</div>
													<div className={style.productName}>{commingSoccerName}</div>
												</div>
											) : null}
									</Fragment>
								)
							}) :
						isSuccess ?
							//NonSportProduct
							(Array.isArray(nonSportProducts) && nonSportProducts.length > 0) || (Array.isArray(thirdPartyProducts) && thirdPartyProducts.length > 0) ? nonSportProducts.map((productProp, productIndex) => {
								return (
									<div
										key={`${productProp.GlobalResourceLabel}_${productIndex}`}
										className={cx('group', style.productItem)}
										data-live={productProp.IsLive}
										data-game={productProp.GameId}
										onClick={() => handleSubProductClick(productProp.RouteTarget!, true)}
									>
										{productProp.isNew ? <NewFeatureBadge /> : null}
										<div className={style.productBg}>
											{
												!productProp.IsLive ? null : <LiveIcon />
											}
											{
												!productProp.BackgroundName ? null : <div className="home-product__game-bg">{productProp.BackgroundName}</div>
											}
											<CdnImg
												width={172}
												height={88}
												src={productProp.Url ? `${SiteConfigsData?.ImageServerUrl}${productProp.Url}` : `/images/home/product-game/${productProp.GameId}.webp`}
												alt=""
												onError={(e) => defaultImageService.setDefaultImageUrl(e, SiteConfigsData?.ImageServerUrl)}
											/>
											<div className={style.productCount}>{productProp.Count}</div>
										</div>
										<div className={style.productName}>{productProp.Name ? productProp.Name : productProp.GlobalResourceLabel}</div>
									</div>
								);
							}) :						
							//ThirdPartyProduct
								templateResource.SubProductResources.map(subProductResource => {
									return (
										<div
											data-name="product_productItem"
											key={subProductResource.Name}
											className={cx('group', style.productItem)}
											onClick={() => {
												if (isBefore) {
													modalService.openLoginModal();
													return;
												}
												if (subProductResource.TheURL) {
													openCasinoRoyale({
														category: subProductResource.TheURL,
														isApp: deepLinkSiteInfo.isApp
													});
												} else {
													const sportId = subProductResource.SportId ? subProductResource.SportId?.split('-')[0] : '';
													const betType = subProductResource.BetType ? String(subProductResource.BetType) : undefined;
													openCasinoRoyale({
														sportTypeId: sportId,
														betTypeId: betType,
														category: subProductResource.TheURL || undefined,
														isApp: deepLinkSiteInfo.isApp
													});
												}
											}}
										>
											<div className={style.productBg}>
												<Image
													width={172}
													height={88}
													src={`${SiteConfigsData?.ImageServerUrl}${subProductResource.ClassName}`}
													alt=""
													onError={(e) => defaultImageService.setDefaultImageUrl(e, SiteConfigsData?.ImageServerUrl)}
												/>
											</div>
											<div className={style.productName}>{subProductResource.Name}</div>
										</div>
									)
								}) : null
				}
			</div>
		</section>
	)
}

const Option = ({
	item
}: {
	item: HomePageProduct
}) => {
	const { Name, ClassName, OpenType, TheURL } = item;
	const { openPredictionMarket } = usePredictionMarket();
	const { openCasinoRoyale } = useCasinoRoyale();
	const deepLinkSiteInfo = useSiteStore(state => state.deepLinkSiteInfo);
	const locale = useLocale();
	const router = useRouter();
	const authToken = useAuthToken();
	const isBefore = !authToken;
	
	const clickEvent = (url: string) => {
		if (isBefore) {
			switch (url.toLowerCase()) {
				case 'vgaming':
				case 'score':
				case 'arcadia':
				case 'advantplay':
				case 'predictionmarket':
					modalService.openLoginModal();
					return;
				default:
					return;
			}
		} else {
			switch (url.toLowerCase()) {
				case 'vgaming':
					openCasinoRoyale({ sportTypeId: '171', isApp: deepLinkSiteInfo.isApp });
					break;
				case 'arcadia':
					openCasinoRoyale({ sportTypeId: '165', isApp: deepLinkSiteInfo.isApp });
					break;
				case 'advantplay':
					openAdvantPlay({ isApp: deepLinkSiteInfo.isApp }, authToken ?? undefined);
					break;
				case 'score':
					router.push('/score', { locale })
					break;
				case 'predictionmarket':
					openPredictionMarket();
					break;
				default:
					return;
			}
		}
	}
	return (
		<>
			<div className={cx('jupiter-home__option-bg', style.optionBg)}></div>
			<div className={style.optionIcon} onClick={() => clickEvent(TheURL!)}>
				<Image
					src={ClassName!}
					width={80}
					height={80}
					alt=""
				/>
				{OpenType ? <div className={style.newWindow}><Icon name="new-window" /></div> : null}
			</div>
			<div className={style.optionText}>{Name}</div>
		</>
	)
}